import { SUMMARY_ITEM_TYPES } from '@mixtiles/web-backend-shared'
import { z } from 'zod'
import { DiscountType } from 'components/Pricing/types/discount.types'
import { PRODUCT_TYPES } from 'services/ProductTypeState'
import { isShippingItem, isTaxItem } from './groupCheckoutPricing.utils'
import {
  normalizeBuyoverTiers,
  RawBuyoverDataSchema,
  RawBuyoverRedemptionSchema,
} from './buyoverTiers'
import { DISCOUNT_TYPES } from 'stores/promoCodeStore/promoCode.consts'
import { logger } from 'services/logger'

const ProductTypeSchema = z.nativeEnum(PRODUCT_TYPES)

const PricingProductSchema = z
  .object({
    tileSize: z.string(),
    materialType: z.string().optional(),
    frameColor: z.string().optional(),
  })
  .passthrough()

export const ProductItemSchema = z.object({
  type: z.literal(SUMMARY_ITEM_TYPES.PRODUCT),
  pricingProduct: PricingProductSchema,
  productId: z.string(),
  priceBeforeDiscount: z.number(),
  priceAfterDiscount: z.number(),
})

export const ShippingItemSchema = z.object({
  type: z.literal(SUMMARY_ITEM_TYPES.SHIPPING),
  shippingType: z.string(),
  shippingOptionName: z.string(),
  priceBeforeDiscount: z.number(),
  priceAfterDiscount: z.number(),
})

const CreditsItemSchema = z.object({
  type: z.literal(SUMMARY_ITEM_TYPES.CREDITS),
  price: z.number(),
})

export const TaxItemSchema = z.object({
  type: z.literal(SUMMARY_ITEM_TYPES.TAX),
  price: z.number(),
  name: z.string(),
})

const RelevantSummaryItemSchema = z.discriminatedUnion('type', [
  ProductItemSchema,
  ShippingItemSchema,
  CreditsItemSchema,
  TaxItemSchema,
])
export const SummaryItemSchema = z
  .object({
    type: z.nativeEnum(SUMMARY_ITEM_TYPES),
  })
  .passthrough()

const RequirementsSchema = z
  .object({
    details: z.array(z.unknown()),
    passed: z.boolean(),
  })
  .passthrough()

const BuyOverDiscountDataSchema = RawBuyoverDataSchema.passthrough()

const ShippingBuyOverDiscountDataSchema = z
  .object({
    eligibleShippingType: z.string(),
    buyOverPrice: z.number(),
    buyOverDiscountPercentage: z.number(),
  })
  .passthrough()

const BundleDiscountDataSchema = z
  .object({
    quantity: z.number(),
    price: z.number(),
  })
  .passthrough()

const SelectedDiscountBaseSchema = z.object({
  id: z.string().optional(),
  discountId: z.string().optional(),
  country: z.string().optional(),
  isAutomatic: z.boolean().optional(),
})

const BuyoverDiscountSchema = SelectedDiscountBaseSchema.extend({
  type: z.literal(DiscountType.BUYOVER),
  buyOverData: BuyOverDiscountDataSchema,
  discountRedemption: RawBuyoverRedemptionSchema.passthrough(),
})

// The tiered-buyover contract downstream tier math relies on, rather than
// re-deriving it.
function validateBuyoverDiscount(
  discount: z.infer<typeof BuyoverDiscountSchema>,
  ctx: z.RefinementCtx
) {
  if (!discount.buyOverData.tiers?.length) return
  const { applied, appliedTierIndex, missingAmountForDiscount } =
    discount.discountRedemption
  if (applied && typeof appliedTierIndex !== 'number') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['discountRedemption', 'appliedTierIndex'],
      message: 'Applied tiered buyover discount must report appliedTierIndex',
    })
  }
  if (missingAmountForDiscount === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['discountRedemption', 'missingAmountForDiscount'],
      message: 'Tiered buyover discount must report missingAmountForDiscount',
    })
  }
}

const ShippingBuyOverDiscountSchema = SelectedDiscountBaseSchema.extend({
  type: z.literal(DISCOUNT_TYPES.SHIPPING_BUY_OVER),
  shippingBuyOverData: ShippingBuyOverDiscountDataSchema.optional(),
  discountRedemption: RawBuyoverRedemptionSchema.passthrough(),
})

const BundleDiscountSchema = SelectedDiscountBaseSchema.extend({
  type: z.literal(DiscountType.BUNDLE),
  bundleData: BundleDiscountDataSchema,
  discountRedemption: z
    .object({
      applied: z.boolean(),
      totalBundlePrice: z.number(),
      quantityForNextDiscount: z.number(),
      appliedBundleCount: z.number(),
    })
    .passthrough(),
})

const PercentDiscountSchema = SelectedDiscountBaseSchema.extend({
  type: z.literal(DiscountType.PERCENT),
  discountRedemption: z
    .object({
      applied: z.boolean(),
      percentOff: z.number(),
    })
    .passthrough(),
})

// BUYXGETY has no fields beyond the base — modeled so it isn't dropped as an unrecognized type.
const BuyXGetYDiscountSchema = SelectedDiscountBaseSchema.extend({
  type: z.literal(DiscountType.BUYXGETY),
})

// UNIT (free-tile credits) has no fields beyond the base — modeled so it isn't dropped as an unrecognized type.
const UnitDiscountSchema = SelectedDiscountBaseSchema.extend({
  type: z.literal(DISCOUNT_TYPES.UNIT),
})

const SelectedDiscountSchema = z
  .discriminatedUnion('type', [
    BuyoverDiscountSchema,
    ShippingBuyOverDiscountSchema,
    BundleDiscountSchema,
    PercentDiscountSchema,
    BuyXGetYDiscountSchema,
    UnitDiscountSchema,
  ])
  .superRefine((discount, ctx) => {
    if (discount.type === DiscountType.BUYOVER) {
      validateBuyoverDiscount(discount, ctx)
    }
  })

type SelectedDiscount = z.infer<typeof SelectedDiscountSchema>

// A discount this frontend doesn't act on (MIX, GIFT_CARD, anything new) or
// one that fails its own schema is simply dropped, so a backend discount
// this frontend can't parse can never break parsing the checkout it's
// attached to.
const SelectedDiscountsSchema = z
  .array(SelectedDiscountBaseSchema.passthrough())
  .transform((rawDiscounts): SelectedDiscount[] => {
    const discounts: SelectedDiscount[] = []
    rawDiscounts.forEach((raw) => {
      const result = SelectedDiscountSchema.safeParse(raw)
      if (!result.success) {
        logger.warning('Failed to parse discount', {
          discount: raw,
          error: result.error,
        })
        return
      }
      discounts.push(result.data)
    })
    return discounts
  })

export const CheckoutResultSchema = z
  .object({
    currency: z.string(),
    key: z.string(),
    summaryItems: z.array(SummaryItemSchema),
    requirements: RequirementsSchema,
    selectedDiscounts: SelectedDiscountsSchema,
    productPriceBeforeDiscount: z.number(),
    priceBeforeTax: z.number(),
    totalCost: z.number(),
    giftsBalances: z.array(z.unknown()),
  })
  .passthrough()

const OrderResultSchema = z
  .object({
    productType: ProductTypeSchema,
    checkoutResults: z.array(CheckoutResultSchema).nonempty(),
  })
  .passthrough()

const GiftCardBalanceSchema = z
  .object({
    code: z.string(),
    balance: z.number(),
    initialValue: z.number(),
  })
  .passthrough()

const GiftCardCreditItemSchema = z
  .object({
    type: z.literal(SUMMARY_ITEM_TYPES.GIFT_CARD),
    price: z.number(),
  })
  .passthrough()

const GroupTotalSchema = z
  .object({
    shippingOption: z.string(),
    totalCost: z.number(),
    priceBeforeTax: z.number(),
    currency: z.string(),
    credits: z.number(),
    giftsRedemptions: z.record(z.number()).optional(),
    giftsBalances: z.array(GiftCardBalanceSchema).optional(),
  })
  .passthrough()

export const GroupPricingProductSchema = z.object({
  productType: ProductTypeSchema,
  // Position of the order this row was priced in, so a row can be attributed
  // to the one cart item whose order it belongs to.
  orderIndex: z.number(),
  pricingProduct: PricingProductSchema,
  productId: z.string(),
  priceBeforeDiscount: z.number(),
  priceAfterDiscount: z.number(),
})

const NormalizedBuyoverTierSchema = z.object({
  price: z.number(),
  discountPercent: z.number(),
  applied: z.boolean(),
  remaining: z.number(),
})

export const BuyoverDataSchema = z.object({
  tiers: z.array(NormalizedBuyoverTierSchema).min(1),
})

export const ShippingBuyoverDataSchema = z.object({
  buyOverPrice: z.number(),
  applied: z.boolean(),
  missingAmountForDiscount: z.number(),
})

const GroupBundleDataSchema = z.object({
  bundleTiles: z.number(),
  bundlePrice: z.number(),
  howManyToBundlePrice: z.number(),
  applied: z.boolean(),
  appliedBundleCount: z.number(),
})

const TaxDetailSchema = z.object({
  amount: z.number(),
  name: z.string(),
})

export const ShippingOptionPricingSchema = z.object({
  totalPrice: z.number(),
  priceBeforeTax: z.number(),
  credits: z.number(),
  creditUsed: z.number(),
  shippingCost: z.number(),
  shippingCostBeforeDiscount: z.number(),
  isEligibleFreeShipping: z.boolean(),
  shippingType: z.string(),
  shippingOptionName: z.string(),
  taxesDetailed: z.array(TaxDetailSchema),
  giftsRedemptions: z.record(z.number()).optional(),
  giftCardCreditItems: z.array(GiftCardCreditItemSchema),
  giftsBalances: z.array(GiftCardBalanceSchema).optional(),
})

export const GroupPricingResultSchema = z.object({
  checkoutId: z.string(),
  products: z.array(GroupPricingProductSchema),
  productTotalAfterDiscount: z.number(),
  productTotalBeforeDiscount: z.number(),
  currency: z.string(),
  buyoverData: BuyoverDataSchema.nullable(),
  shippingBuyoverData: ShippingBuyoverDataSchema.nullable(),
  bundleData: GroupBundleDataSchema.nullable(),
  shippingOptions: z.record(ShippingOptionPricingSchema),
  percentOff: z.number().nullable(),
})

const GroupTotalsSchema = z
  .array(GroupTotalSchema)
  .nonempty()
  .refine(
    ([firstGroupTotal, ...groupTotals]) =>
      groupTotals.every(
        (groupTotal) => groupTotal.currency === firstGroupTotal.currency
      ),
    { message: 'Expected all group totals to use the same currency' }
  )

const RawGroupCheckoutResponseSchema = z
  .object({
    checkoutId: z.string(),
    orderResults: z.array(OrderResultSchema).nonempty(),
    groupTotals: GroupTotalsSchema,
  })
  .passthrough()
  .superRefine((response, ctx) => {
    const currency = response.groupTotals[0].currency

    response.orderResults.forEach((orderResult, orderIndex) => {
      orderResult.checkoutResults.forEach((checkoutResult, checkoutIndex) => {
        if (checkoutResult.currency !== currency) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [
              'orderResults',
              orderIndex,
              'checkoutResults',
              checkoutIndex,
              'currency',
            ],
            message: 'Expected checkout result currency to match group totals',
          })
        }
      })
    })
  })

type OrderResult = z.infer<typeof OrderResultSchema>
type CheckoutResult = z.infer<typeof CheckoutResultSchema>
type GroupTotal = z.infer<typeof GroupTotalSchema>
type SummaryItem = z.infer<typeof SummaryItemSchema>
type RelevantSummaryItem = z.infer<typeof RelevantSummaryItemSchema>
type GroupPricingProduct = z.infer<typeof GroupPricingProductSchema>
type GroupBuyoverData = z.infer<typeof BuyoverDataSchema>
type GroupShippingBuyoverData = z.infer<typeof ShippingBuyoverDataSchema>
type ShippingOptionPricing = z.infer<typeof ShippingOptionPricingSchema>
type GroupBundleData = z.infer<typeof GroupBundleDataSchema>
type GroupPricingResult = z.infer<typeof GroupPricingResultSchema>

function isRelevantSummaryItemType(
  type: unknown
): type is
  | typeof SUMMARY_ITEM_TYPES.PRODUCT
  | typeof SUMMARY_ITEM_TYPES.SHIPPING
  | typeof SUMMARY_ITEM_TYPES.CREDITS
  | typeof SUMMARY_ITEM_TYPES.TAX {
  return (
    type === SUMMARY_ITEM_TYPES.PRODUCT ||
    type === SUMMARY_ITEM_TYPES.SHIPPING ||
    type === SUMMARY_ITEM_TYPES.CREDITS ||
    type === SUMMARY_ITEM_TYPES.TAX
  )
}

function parseRelevantSummaryItems(
  items: SummaryItem[]
): RelevantSummaryItem[] {
  return items.flatMap((item) => {
    if (!isRelevantSummaryItemType(item.type)) {
      return []
    }

    return [RelevantSummaryItemSchema.parse(item)]
  })
}

function getSharedCheckoutResult(orderResult: OrderResult): CheckoutResult {
  // Product pricing and selected discounts are duplicated across shipping options.
  return orderResult.checkoutResults[0]
}

function extractProducts(orderResults: OrderResult[]): GroupPricingProduct[] {
  const products: GroupPricingProduct[] = []
  orderResults.forEach((orderResult, orderIndex) => {
    const sharedResult = getSharedCheckoutResult(orderResult)
    for (const item of parseRelevantSummaryItems(sharedResult.summaryItems)) {
      if (item.type === SUMMARY_ITEM_TYPES.PRODUCT) {
        products.push({
          productType: orderResult.productType,
          orderIndex,
          pricingProduct: item.pricingProduct,
          productId: item.productId,
          priceBeforeDiscount: item.priceBeforeDiscount,
          priceAfterDiscount: item.priceAfterDiscount,
        })
      }
    }
  })
  return products
}

function extractBuyoverData(
  selectedDiscounts: SelectedDiscount[],
  productTotalAfterDiscount: number
): GroupBuyoverData | null {
  const buyover = selectedDiscounts.find((d) => d.type === DiscountType.BUYOVER)
  if (!buyover) return null

  return {
    tiers: normalizeBuyoverTiers({
      buyOverData: buyover.buyOverData,
      discountRedemption: buyover.discountRedemption,
      productTotal: productTotalAfterDiscount,
    }),
  }
}

// The progress UI only renders for standard (FREE) shipping, so the buyover
// data must come from the standard-eligible discount.
const STANDARD_ELIGIBLE_SHIPPING_TYPE = 'standard'

function extractShippingBuyoverData(
  selectedDiscounts: SelectedDiscount[],
  productTotalAfterDiscount: number
): GroupShippingBuyoverData | null {
  // The backend can return several SHIPPING_BUY_OVER discounts at once (e.g. one
  // for 'standard' and one for 'pickup' shipping) in an unspecified order. Match
  // the standard entry explicitly so buyOverPrice and applied both come from it —
  // taking the first match would surface the wrong threshold/eligibility.
  const discount = selectedDiscounts
    .filter((d) => d.type === DISCOUNT_TYPES.SHIPPING_BUY_OVER)
    .find(
      (d) =>
        d.shippingBuyOverData?.eligibleShippingType ===
        STANDARD_ELIGIBLE_SHIPPING_TYPE
    )
  if (!discount?.shippingBuyOverData) return null

  const { buyOverPrice } = discount.shippingBuyOverData
  const applied =
    discount.discountRedemption.applied ||
    productTotalAfterDiscount >= buyOverPrice
  return {
    buyOverPrice,
    applied,
    missingAmountForDiscount: applied
      ? 0
      : Math.max(buyOverPrice - productTotalAfterDiscount, 0),
  }
}

function buildGiftCardCreditItems(
  giftsRedemptions: Record<string, number> = {}
) {
  // Fallback for group totals: giftsRedemptions is keyed by code with positive
  // redeemed amounts, while checkout summary GIFT_CARD rows use negative prices.
  return Object.values(giftsRedemptions).map((amount) => ({
    type: SUMMARY_ITEM_TYPES.GIFT_CARD as const,
    price: -amount,
  }))
}

function buildShippingOptions(
  orderResults: OrderResult[],
  groupTotals: GroupTotal[]
): Record<string, ShippingOptionPricing> {
  const shippingOptions: Record<string, ShippingOptionPricing> = {}
  for (const groupTotal of groupTotals) {
    let shippingCost = 0
    let shippingCostBeforeDiscount = 0
    let shippingType: string | null = null
    let shippingOptionName: string | null = null
    const groupTotalTaxByName = new Map<string, number>()
    for (const orderResult of orderResults) {
      const result = orderResult.checkoutResults.find(
        (r) => r.key === groupTotal.shippingOption
      )
      if (!result) {
        throw new Error(
          `Missing checkout result for shipping option ${groupTotal.shippingOption}`
        )
      }

      const relevantItems = parseRelevantSummaryItems(result.summaryItems)

      const shippingItems = relevantItems.filter(isShippingItem)

      if (shippingItems.length === 0) {
        throw new Error(
          `Missing shipping summary item for shipping option ${groupTotal.shippingOption}`
        )
      }
      if (shippingItems.length > 1) {
        throw new Error(
          `Expected one shipping summary item for shipping option ${groupTotal.shippingOption}, received ${shippingItems.length}`
        )
      }

      const [shippingItem] = shippingItems
      if (shippingType !== null && shippingType !== shippingItem.shippingType) {
        throw new Error(
          `Inconsistent shipping type for shipping option ${groupTotal.shippingOption}`
        )
      }
      if (
        shippingOptionName !== null &&
        shippingOptionName !== shippingItem.shippingOptionName
      ) {
        throw new Error(
          `Inconsistent shipping option name for shipping option ${groupTotal.shippingOption}`
        )
      }

      shippingType = shippingItem.shippingType
      shippingOptionName = shippingItem.shippingOptionName
      shippingCostBeforeDiscount += shippingItem.priceBeforeDiscount
      shippingCost += shippingItem.priceAfterDiscount

      for (const item of relevantItems.filter(isTaxItem)) {
        // Accumulate tax amounts by name across all orders in the group
        const accumulatedTax = groupTotalTaxByName.get(item.name) ?? 0
        groupTotalTaxByName.set(item.name, accumulatedTax + item.price)
      }
    }

    if (!shippingType || !shippingOptionName) {
      throw new Error(
        `Missing shipping metadata for shipping option ${groupTotal.shippingOption}`
      )
    }

    const taxesDetailed = [...groupTotalTaxByName.entries()].map(
      ([name, amount]) => ({
        amount,
        name,
      })
    )
    const giftsRedemptions = groupTotal.giftsRedemptions ?? {}

    shippingOptions[groupTotal.shippingOption] = {
      totalPrice: groupTotal.totalCost,
      priceBeforeTax: groupTotal.priceBeforeTax,
      credits: groupTotal.credits,
      creditUsed: Math.abs(groupTotal.credits),
      shippingCost,
      shippingCostBeforeDiscount,
      isEligibleFreeShipping: shippingCost === 0,
      shippingType,
      shippingOptionName,
      giftsRedemptions,
      giftCardCreditItems: buildGiftCardCreditItems(giftsRedemptions),
      giftsBalances: groupTotal.giftsBalances ?? [],
      taxesDetailed,
    }
  }
  return shippingOptions
}

function extractBundleData(
  selectedDiscounts: SelectedDiscount[]
): GroupBundleData | null {
  const bundle = selectedDiscounts.find((d) => d.type === DiscountType.BUNDLE)
  if (!bundle) return null

  const { quantity, price } = bundle.bundleData
  const { applied, quantityForNextDiscount, appliedBundleCount } =
    bundle.discountRedemption
  return {
    bundleTiles: quantity,
    bundlePrice: price,
    howManyToBundlePrice: quantityForNextDiscount,
    applied,
    appliedBundleCount,
  }
}

function extractPercentOff(
  selectedDiscounts: SelectedDiscount[]
): number | null {
  const percentDiscount = selectedDiscounts
    .filter((d) => d.type === DiscountType.PERCENT)
    .find((d) => d.discountRedemption.applied)
  return percentDiscount?.discountRedemption.percentOff || null
}

function normalizeGroupCheckoutPricing(
  response: z.infer<typeof RawGroupCheckoutResponseSchema>
): GroupPricingResult {
  const products = extractProducts(response.orderResults)
  const productTotalAfterDiscount = products.reduce(
    (sum, p) => sum + p.priceAfterDiscount,
    0
  )
  const productTotalBeforeDiscount = products.reduce(
    (sum, p) => sum + p.priceBeforeDiscount,
    0
  )
  // Discounts are attached to specific orders (e.g. the free-shipping buyover
  // lives on the tiles order, not the photo book), so merge across the whole
  // group rather than assuming they all live on the first order.
  const selectedDiscounts = response.orderResults.flatMap(
    (orderResult) => getSharedCheckoutResult(orderResult).selectedDiscounts
  )
  const buyoverData = extractBuyoverData(
    selectedDiscounts,
    productTotalAfterDiscount
  )
  const shippingBuyoverData = extractShippingBuyoverData(
    selectedDiscounts,
    productTotalAfterDiscount
  )
  const bundleData = extractBundleData(selectedDiscounts)
  const shippingOptions = buildShippingOptions(
    response.orderResults,
    response.groupTotals
  )
  const percentOff = extractPercentOff(selectedDiscounts)

  return {
    checkoutId: response.checkoutId,
    products,
    productTotalAfterDiscount,
    productTotalBeforeDiscount,
    currency: response.groupTotals[0].currency,
    buyoverData,
    shippingBuyoverData,
    bundleData,
    shippingOptions,
    percentOff,
  }
}

export const GroupCheckoutResponseSchema =
  RawGroupCheckoutResponseSchema.transform(normalizeGroupCheckoutPricing).pipe(
    GroupPricingResultSchema
  )
